import { useState, useEffect } from "react";
import AdminSidebar from "@/pages/admin/components/AdminSidebar";
import { getSupabase } from "@/lib/supabase";

interface UserProfile {
  id: string;
  username: string | null;
  display_name: string | null;
  email: string | null;
  role: string;
  bio: string | null;
  created_at: string;
}

export default function AdminUsersPage() {
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [editingUser, setEditingUser] = useState<UserProfile | null>(null);
  const [editForm, setEditForm] = useState({ display_name: "", username: "", role: "", bio: "" });
  const [saveStatus, setSaveStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [saveError, setSaveError] = useState("");
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const supabase = getSupabase();
      const { data } = await supabase.from("profiles").select("*").order("created_at", { ascending: false });
      setUsers(data || []);
    } catch {
      // fallback
    } finally {
      setLoading(false);
    }
  };

  const openEdit = (user: UserProfile) => {
    setEditingUser(user);
    setEditForm({
      display_name: user.display_name || "",
      username: user.username || "",
      role: user.role || "client",
      bio: user.bio || "",
    });
    setSaveStatus("idle");
    setSaveError("");
  };

  const handleSave = async () => {
    if (!editingUser) return;
    setSaveStatus("loading");
    setSaveError("");
    try {
      const supabase = getSupabase();
      const { error } = await supabase
        .from("profiles")
        .update({
          display_name: editForm.display_name,
          username: editForm.username,
          role: editForm.role,
          bio: editForm.bio,
          updated_at: new Date().toISOString(),
        })
        .eq("id", editingUser.id);
      if (error) throw error;
      setSaveStatus("success");
      setUsers((prev) =>
        prev.map((u) =>
          u.id === editingUser.id
            ? { ...u, display_name: editForm.display_name, username: editForm.username, role: editForm.role, bio: editForm.bio }
            : u
        )
      );
      setTimeout(() => {
        setEditingUser(null);
        setSaveStatus("idle");
      }, 1200);
    } catch (err: unknown) {
      setSaveStatus("error");
      setSaveError(err instanceof Error ? err.message : "Failed to save");
    }
  };

  const handleDelete = async (userId: string) => {
    try {
      const supabase = getSupabase();
      await supabase.from("profiles").delete().eq("id", userId);
      setUsers((prev) => prev.filter((u) => u.id !== userId));
      setConfirmDeleteId(null);
    } catch {
      // fallback
    }
  };

  const filteredUsers = users.filter(
    (u) =>
      (u.display_name || "").toLowerCase().includes(search.toLowerCase()) ||
      (u.email || "").toLowerCase().includes(search.toLowerCase()) ||
      (u.username || "").toLowerCase().includes(search.toLowerCase())
  );

  const roleBadge = (role: string) => {
    if (role === "admin") return "bg-red-900/30 text-red-400";
    if (role === "hacker") return "bg-purple-100 text-purple-700";
    return "bg-[#1c2128] text-gray-400";
  };

  return (
    <div className="min-h-screen bg-[#161b22]">
      <AdminSidebar />
      <div className="ml-60">
        <header className="bg-[#0d1117] border-b border-gray-700 px-6 py-4 flex items-center justify-between sticky top-0 z-30">
          <div>
            <h1 className="text-lg font-bold text-gray-200">Users</h1>
            <p className="text-xs text-gray-500">{users.length} total users</p>
          </div>
          <div className="relative">
            <i className="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search users..."
              className="pl-9 pr-4 py-2 border border-gray-700 rounded-lg text-xs w-56 focus:outline-none focus:border-[#5cb85c]"
            />
          </div>
        </header>

        <main className="p-6">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <i className="fas fa-spinner fa-spin text-[#5cb85c] text-2xl" />
            </div>
          ) : (
            <div className="bg-[#0d1117] rounded-xl border border-gray-800 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-[#161b22] border-b border-gray-800">
                      <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase">User</th>
                      <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase hidden md:table-cell">Email</th>
                      <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Role</th>
                      <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase hidden lg:table-cell">Joined</th>
                      <th className="text-right px-5 py-3 text-xs font-semibold text-gray-500 uppercase w-32">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {filteredUsers.map((user) => (
                      <tr key={user.id} className="hover:bg-[#161b22]/50">
                        <td className="px-5 py-3.5">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-[#5cb85c]/10 flex items-center justify-center text-[#5cb85c] text-xs font-bold">
                              {(user.display_name || user.username || "U").charAt(0).toUpperCase()}
                            </div>
                            <div>
                              <p className="text-sm font-semibold text-gray-200">{user.display_name || user.username || "Unnamed"}</p>
                              <p className="text-[11px] text-gray-400">@{user.username || "—"}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-5 py-3.5 hidden md:table-cell">
                          <span className="text-xs text-gray-500">{user.email || "—"}</span>
                        </td>
                        <td className="px-5 py-3.5">
                          <span className={`text-[11px] font-semibold px-2.5 py-1 rounded-full ${roleBadge(user.role)}`}>
                            {user.role}
                          </span>
                        </td>
                        <td className="px-5 py-3.5 hidden lg:table-cell">
                          <span className="text-xs text-gray-400">
                            {user.created_at ? new Date(user.created_at).toLocaleDateString() : "—"}
                          </span>
                        </td>
                        <td className="px-5 py-3.5 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <button
                              onClick={() => openEdit(user)}
                              className="w-7 h-7 rounded-lg hover:bg-[#1c2128] flex items-center justify-center text-gray-400 hover:text-[#5cb85c] transition-all"
                              title="Edit"
                            >
                              <i className="fas fa-edit text-xs" />
                            </button>
                            <button
                              onClick={() => setConfirmDeleteId(user.id)}
                              className="w-7 h-7 rounded-lg hover:bg-red-900/20 flex items-center justify-center text-gray-400 hover:text-red-500 transition-all"
                              title="Delete"
                            >
                              <i className="fas fa-trash-alt text-xs" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {filteredUsers.length === 0 && (
                      <tr>
                        <td colSpan={5} className="px-5 py-12 text-center text-sm text-gray-400">
                          No users found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </main>
      </div>

      {/* Edit Modal */}
      {editingUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setEditingUser(null)}>
          <div className="bg-[#0d1117] rounded-xl w-full max-w-md mx-4 p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-base font-bold text-gray-200">Edit User</h3>
              <button onClick={() => setEditingUser(null)} className="w-7 h-7 rounded-lg hover:bg-[#1c2128] flex items-center justify-center text-gray-400">
                <i className="fas fa-times text-sm" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-gray-400 mb-1">Display Name</label>
                <input
                  type="text"
                  value={editForm.display_name}
                  onChange={(e) => setEditForm({ ...editForm, display_name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-700 rounded-lg text-sm focus:outline-none focus:border-[#5cb85c]"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-400 mb-1">Username</label>
                <input
                  type="text"
                  value={editForm.username}
                  onChange={(e) => setEditForm({ ...editForm, username: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-700 rounded-lg text-sm focus:outline-none focus:border-[#5cb85c]"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-400 mb-1">Role</label>
                <select
                  value={editForm.role}
                  onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-700 rounded-lg text-sm focus:outline-none focus:border-[#5cb85c]"
                >
                  <option value="client">Client</option>
                  <option value="hacker">Hacker</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-400 mb-1">Bio</label>
                <textarea
                  value={editForm.bio}
                  onChange={(e) => setEditForm({ ...editForm, bio: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-700 rounded-lg text-sm focus:outline-none focus:border-[#5cb85c] resize-none"
                />
              </div>
            </div>
            {saveStatus === "error" && (
              <p className="mt-3 text-xs text-red-500">{saveError}</p>
            )}
            <div className="flex gap-3 mt-5">
              <button onClick={() => setEditingUser(null)} className="flex-1 px-4 py-2.5 border border-gray-700 rounded-lg text-sm font-semibold text-gray-400 hover:bg-[#161b22] transition-all whitespace-nowrap">
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saveStatus === "loading"}
                className="flex-1 px-4 py-2.5 bg-[#5cb85c] hover:bg-[#4cae4c] disabled:bg-gray-700 text-white text-sm font-semibold rounded-lg transition-all whitespace-nowrap"
              >
                {saveStatus === "loading" ? "Saving..." : saveStatus === "success" ? "Saved!" : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      {confirmDeleteId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setConfirmDeleteId(null)}>
          <div className="bg-[#0d1117] rounded-xl w-full max-w-sm mx-4 p-6" onClick={(e) => e.stopPropagation()}>
            <div className="w-12 h-12 bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="fas fa-exclamation-triangle text-red-500" />
            </div>
            <h3 className="text-base font-bold text-gray-200 text-center mb-2">Delete User?</h3>
            <p className="text-xs text-gray-500 text-center mb-5">This action cannot be undone. The user account will be permanently removed.</p>
            <div className="flex gap-3">
              <button onClick={() => setConfirmDeleteId(null)} className="flex-1 px-4 py-2.5 border border-gray-700 rounded-lg text-sm font-semibold text-gray-400 hover:bg-[#161b22] transition-all whitespace-nowrap">
                Cancel
              </button>
              <button onClick={() => handleDelete(confirmDeleteId)} className="flex-1 px-4 py-2.5 bg-red-500 hover:bg-red-600 text-white text-sm font-semibold rounded-lg transition-all whitespace-nowrap">
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}